// @ts-expect-error
import ShapesSDK from '@omneo/shapes-sdk'
import { InterfaceShapesSdk, OmneoList, ShapesResponse, OmneoListItemMeta, OmneoCommsPreferences, OmneoUpdateCommsPreferences } from '../types'
import { attachRequiredStorefrontInformationToOmneoItem, getStorefrontProductsBySkus, itemShouldBeHidden, listMadeOnSiteIsValid } from './utils'
// want to import this, but have an "indefinite assignment in constructor" ,

/**
 * Relevant documentation:
 * -  Shapes SDK: https://shapes-sdk.omneo.io/guide/#find
 * -  REST API docs: https://omneo.readme.io/reference/indexlistitems
 *    - Please note that the Shapes SDK documentation does not contain sufficient
 * information to get success responses. Use the SDK to learn about how to
 * structure the dot path notation and which keywords are available, use the REST
 * docs for types and understanding validation.
 */

declare global {
  interface Window {
    omneoService: OmneoService
  }
}

export class OmneoListItem {
  constructor({ sku, meta }: { sku: string; meta: OmneoListItemMeta }) {
    this.product_variant_sku = sku
    this.meta = meta
  }
  product_variant_sku: string
  meta: OmneoListItemMeta
}

export class OmneoService {
  sdk: InterfaceShapesSdk
  _b_t: string
  #_p_t: string

  constructor(privateToken: string, publicToken: string) {
    try {
      this.sdk = ShapesSDK.init({
        url: CD_STATE.omneo.rootUrl,
        token: privateToken,
        logging: false
      })
    } catch (err) {
      console.warn(
        'Error during ShapesSDK init',
        { err },
        {
          privateToken,
          publicToken,
          ShapesSDK
        }
      )
      console.warn('Retrying Omneo SDK init via catch statement; see error above for details;')
      this.sdk = ShapesSDK.init({
        url: CD_STATE.omneo.rootUrl,
        token: privateToken,
        logging: false
      })
    }

    // required for shared wishlist functionality, do not use elsewhere please
    this._b_t = publicToken
    // intermittent issues on
    this.#_p_t = privateToken
    return this
  }

  async reInitSDK() {
    this.sdk = ShapesSDK.init({
      url: CD_STATE.omneo.rootUrl,
      token: this.#_p_t,
      logging: false
    })
  }

  fallbackError(errorObj: unknown, invoker: string): void {
    console.error('Could not get via', invoker, errorObj)
  }

  async getListHandles(): Promise<Array<string>> {
    const lists = await this.getLists()
    if (!lists.length) return []

    return lists.map((x) => x.handle)
  }

  async getLists(): Promise<Array<OmneoList>> {
    if (!this.sdk.get) {
      console.error('Omneo SDK not defined; attempting reinit')
      await this.reInitSDK()
    }
    const res: ShapesResponse = await this.sdk.get('lists').catch((err: ShapesResponse) => err)

    if (res.error) {
      this.fallbackError(res, 'getLists')
      return []
    }

    const lists = (res.body.data as OmneoList[])
      // exclude zendesk appointment;
      .filter((list) => list.definition.handle !== 'zendesk-ticket')
      // exclude conventional lists where all items have no meta
      // this would be considered malformed
      .filter((list) => listMadeOnSiteIsValid(list))
    // order by last updated (most recent first); uses most recently created item, with a fallback of list updated_at

    lists.forEach((list) => {
      const mostRecentItemCreate = list.items.toSorted((itemA, itemB) => (itemA.created_at > itemB.created_at ? -1 : 1))[0]?.created_at || list.updated_at
      list.lastUpdate = mostRecentItemCreate
    })

    // @ts-expect-error
    lists.sort((a, b) => (a.lastUpdate > b.lastUpdate ? -1 : 1))

    // Need to get handles for in store lists
    const skusNeedingHandles = lists
      .flatMap((list) => list.items)
      .filter((item) => !itemShouldBeHidden(item))
      .map((item) => item.product_variant.sku)

    const inStoreItems = [...new Set(skusNeedingHandles)]

    const storefrontProducts = await getStorefrontProductsBySkus(inStoreItems)

    // for debugging
    // of the in store items, which skus cannot be found in the list of storefront products
    const skusNeedingHandlesNotFound = skusNeedingHandles.filter((sku) => !storefrontProducts.some((product) => product.skus.includes(sku)))
    console.error('Could not find products for these skus during getLists', { skusNeedingHandlesNotFound, storefrontProducts })

    // it is possible to delete a handle - this should be handled in the List component
    for (const list of lists) {
      for (const item of list.items) {
        if (itemShouldBeHidden(item)) continue
        attachRequiredStorefrontInformationToOmneoItem(item, storefrontProducts, list.definition.type)
      }
    }

    const listsWithMissingMeta = lists.filter((list) => !listMadeOnSiteIsValid(list))
    if (listsWithMissingMeta.length) {
      console.error('Found lists with no item meta', listsWithMissingMeta)
    }

    return lists
  }

  async getListByHandle(handle: string): Promise<OmneoList | null> {
    if (!handle) {
      console.error('Must provide a handle to getListsByHandle', handle)
      return null
    }

    const res: ShapesResponse = await this.sdk.get(`lists.${handle}`).catch((err: ShapesResponse) => err)

    if (res.error) {
      this.fallbackError(res, 'getLists')
      return null
    }
    return res.body.data as OmneoList
  }

  async createList(name: string, initialItem?: OmneoListItem | null): Promise<OmneoList | null> {
    if (!name) {
      console.error('Must provide a name to createList', name)
      return null
    }

    const handle = name.replace(/[^a-z0-9]/g, '-').replace(/(--)/g, '-')

    const newList: Partial<OmneoList> = { name, handle, list_definition_id: 1 }
    if (initialItem) {
      // could be fixed by either creating a new type or changing the union type to an optional union - not sure how to do that
      // @ts-expect-error this is the correct type for creating an item; the missing props, created_at and updated_at, are added on the server side;
      newList.items = [initialItem]
    }

    if (!this.sdk.post) {
      console.error('Omneo SDK not defined; attempting reinit')
      await this.reInitSDK()
    }


    const res = await this.sdk.post('lists', newList).catch((err: ShapesResponse) => err)

    if (res.error) {
      console.log('res.error', res.error)
      this.fallbackError(res, 'createList')
      return null
    }

    console.log('res: ', res)

    return res.body.data as OmneoList
  }

  async updateList(id: string | number, name: string): Promise<OmneoList | null> {
    if (!id) {
      console.error('Must provide an id to updateList', id)
      return null
    }

    const res = await this.sdk.put(`lists.${id}`, { name }).catch((err: ShapesResponse) => err)

    if (res.error) {
      this.fallbackError(res, 'updateList')
      return null
    }

    return res.body.data as OmneoList
  }

  /**
   *
   * @param id .id prop
   * @returns { boolean } the list was deleted
   */
  async deleteList(id: string | number): Promise<boolean> {
    if (!id) {
      console.error('Must provide an id to removeList', id)
      return false
    }

    const res = await this.sdk.delete(`lists.${id}`).catch((err: ShapesResponse) => err)

    if (res.error) {
      this.fallbackError(res, 'removeList')
      return false
    }
    return !res.error
  }

  getOmneoItem(minimumProps: OmneoListItemMeta, additionalProps?: { [name: string]: string }): OmneoListItem {
    let metaProps = minimumProps
    if (additionalProps) {
      metaProps = {
        ...metaProps,
        ...additionalProps
      }
    }
    return new OmneoListItem({ sku: minimumProps.variantSku, meta: metaProps })
  }

  /**
   *
   * @param listHandle
   * @param profileListItem
   * @returns
   */
  async addItemToList(listHandle: string, profileListItem: OmneoListItem) {
    const path = `lists.${listHandle}.items`

    const res = await this.sdk.post(path, profileListItem).catch((err: ShapesResponse) => err)

    // this works:
    // let aa = omneoService.sss // copy of the shapes SDK
    // let path = 'lists.1.items'
    // let item = { product_variant_sku: 'the sku', title: 'New List Item' }
    // await aa.put(path,item,true)

    if (res.error) {
      this.fallbackError(res, 'addItemToList')
      return false
    }
    return res
  }

  /**
   *
   * @param listHandle
   * @param listItemId
   */
  async removeItemFromList(listHandle: string, listItemId: number) {
    if (!listHandle || !listItemId) {
      console.error('Must provide listHandle and listItemId to removeItemFromList', listHandle, listItemId)
      return
    }

    const path = `lists.${listHandle}.items.${listItemId}`

    const res = await this.sdk.delete(path).catch((err: ShapesResponse) => err)

    if (res.error) {
      this.fallbackError(res, 'removeItemFromList')
      return false
    }
    return res
  }

  async shareWishlist(listId: string | number) {
    if (!listId) {
      console.error('Must provide id to shareWishlist', listId)
      return
    }

    const path = `lists.${listId}.shares`

    const res = await this.sdk.post(path).catch((err: ShapesResponse) => err)

    if (res.error) {
      this.fallbackError(res, 'shareWishlist')
      return false
    }

    return res
  }

  async deleteWishlistShare(listId: string | number, shareId: number) {
    if (!listId || !shareId) {
      console.error('Must provide listId and shareId to deleteWishlistShare', listId, shareId)
      return
    }
    const path = `lists.${listId}.shares.${shareId}`
    const res = await this.sdk.delete(path).catch((err: ShapesResponse) => err)
    if (res.error) {
      this.fallbackError(res, 'deleteWishlistShare')
      return false
    }
    return res
  }

  async getCommsPreferences(): Promise<OmneoUpdateCommsPreferences | null> {
    if (!this.sdk.get) {
      console.error('Omneo SDK not defined; attempting reinit')
      await this.reInitSDK()
    }
    const res = await this.sdk.get('profile').catch((err: ShapesResponse) => err)

    if (res.error) {
      this.fallbackError(res, 'getLists')
      return null
    }
    const { comms } = res.body.data.attributes
    let { email_promo, email_account, email_service, email_optout, sms_promo, sms_account, sms_service, sms_optout } = comms
    // Have to redefine these variables as false as optout value of true does not update the other values
    if (email_optout) {
      email_promo = false
      email_account = false
      email_service = false
    }
    if (sms_optout) {
      sms_promo = false
      sms_account = false
      sms_service = false
    }
    return { email_promo, email_account, email_service, email_optout, sms_promo, sms_account, sms_service, sms_optout } as OmneoUpdateCommsPreferences
  }

  // : Promise<OmneoCommsPreferences>
  async setCommsPreferences(newPreferences: Partial<OmneoUpdateCommsPreferences>) {
    if (!newPreferences) {
      console.error('Must provide newPreferences to setCommsPreferences', newPreferences)
      return null
    }

    // Check if it newPreferences.email_optout is true, then set all email preferences to false
    if (newPreferences.email_optout) {
      newPreferences.email_promo = false
      newPreferences.email_account = false
      newPreferences.email_service = false
    }

    // Check if it newPreferences.sms_optout is true, then set all sms preferences to false
    if (newPreferences.sms_optout) {
      newPreferences.sms_promo = false
      newPreferences.sms_account = false
      newPreferences.sms_service = false
    }

    const path = 'profile'
    const payload = {
      attributes: {
        comms: newPreferences
      }
    }

    const res = await this.sdk.put(path, payload, true)

    if (res.error) {
      this.fallbackError(res, 'setCommsPreferences')
      return null
    }

    return res.body.data.attributes.comms as OmneoCommsPreferences
  }
}
